import React, { useEffect, useMemo, useState } from 'react';
import API from '../../api';
import '../css/callbackcomponent.css';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, setUser } from '../../store/authSlice';
import LeadDetailsModal from './LeadDetailsModal';

/** Normalize one record from your API to fields we render. */
function mapLead(record, idx) {
  const id = record.id ?? idx;
  const name = record.name || 'Untitled';
  const mobile = record.mobile || '';
  const agent = record.agent || '';
  const status = record.status || '';
  const bname = record.bname || '';
  const scomment = record.scomment || '';
  const timezone = record.tmzone || '';
  const ass = record.ass ?? '';




  let calldate = null;
  try {
    if (record.calldate) {
      if (record.calltime) {
        const d = new Date(record.calldate);
        const [hh = '00', mm = '00', ss = '00'] = String(record.calltime).split(':');
        // d.setUTCHours(Number(hh), Number(mm), Number(ss || '00'));
        calldate = d;
      } else {
        calldate = new Date(record.calldate);
      }
    }
  } catch {
    calldate = null;
  }

  return { id, name, mobile, agent, timezone, ass, status, bname, calldate, calltime: record.calltime || null, scomment };

}

function formatCallDate(d) {
  if (!d) return null;
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: EST_TZ,
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return null;
  }
}



// function startOfDay(dt = new Date()) {
//   const d = new Date(dt);
//   d.setHours(0, 0, 0, 0);
//   return d;
// }
// function endOfDay(dt = new Date()) {
//   const d = new Date(dt);
//   d.setHours(23, 59, 59, 999);
//   return d;
// }

const EST_TZ = 'America/New_York';

// Extract EST calendar parts (DST-safe)
function getESTParts(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const get = (type) => parts.find(p => p.type === type)?.value;
  return {
    y: Number(get('year')),
    m: Number(get('month')),
    d: Number(get('day')),
  };
}

// START of day in New York (EST/EDT)
function startOfDay(dt = new Date()) {
  const { y, m, d } = getESTParts(dt);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

// END of day in New York (EST/EDT)
function endOfDay(dt = new Date()) {
  const { y, m, d } = getESTParts(dt);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function getCallDateTime(row) {
  if (!row.calldate) return null;

  const d = new Date(row.calldate);

  if (row.calltime) {
    const [hh = '0', mm = '0', ss = '0'] = String(row.calltime).split(':');
    d.setHours(Number(hh), Number(mm), Number(ss));
  } else {
    d.setHours(0, 0, 0, 0);
  }

  return d;
}



export default function ApproveComponent() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // NEW: modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalLead, setModalLead] = useState(null);
  const [callDateSort, setCallDateSort] = useState(null); // null | 'asc' | 'desc'


  // ---- Filters ----
  const [agentFilter, setAgentFilter] = useState('All');

  // dateMode: 'All' | 'Past' | 'Upcoming' | 'Today' | 'Range'
  const [dateMode, setDateMode] = useState('All');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // Pagination
  const [pageSize, setPageSize] = useState(50); // 50 or 100
  const [page, setPage] = useState(1);


  useEffect(() => {
    setPage(1);
  }, [agentFilter, dateMode, rangeStart, rangeEnd, rows]);



  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!user) {
          const { data } = await API.get('/auth/me');
          if (!ignore && data?.user) dispatch(setUser(data.user));
        }
      } catch (_) { }
    })();
    return () => { ignore = true; };
  }, [user, dispatch]);



  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');

        // pick endpoint + params based on role
        let endpoint = '/auth/leads';
        let params = {};
        const role = user?.role?.toLowerCase();

        if (role === 'sub-admin') {
          endpoint = '/auth/app-sub-admin';
          if (user?.ass != null) params.ass = user.ass;   // send ass
        } else if (role === 'admin') {
          endpoint = '/auth/app-admin';
        } else if (role === 'agent') {
          endpoint = '/auth/app-agent';
          if (user?.id != null) params.uid = user.id;     // send uid
        }

        const resp = await API.get(endpoint, { params });
        const data = resp?.data;


        const list =
          (Array.isArray(data) && data) ||
          (Array.isArray(data?.data) && data.data) ||
          (Array.isArray(data?.results) && data.results) ||
          (Array.isArray(data?.items) && data.items) ||
          (Array.isArray(data?.leads) && data.leads) ||
          [];

        const mapped = list.map(mapLead);
        if (!ignore) setRows(mapped);
      } catch (e) {
        console.error(e);
        if (!ignore) setErr('Could not load items.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const allSelected = useMemo(
    () => rows.length > 0 && rows.every(r => selectedIds.has(r.id)),
    [rows, selectedIds]
  );

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => {
      if (rows.length === 0) return prev;
      const every = rows.every(r => prev.has(r.id));
      if (every) return new Set(); // clear
      const next = new Set(prev);
      rows.forEach(r => next.add(r.id));
      return next;
    });
  };

  // Build distinct agent list
  const agentOptions = useMemo(
    () => Array.from(new Set(rows.map(r => r.agent).filter(Boolean))).sort(),
    [rows]
  );

  // Apply filters
  const filteredRows = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);

    return rows.filter(r => {
      // agent
      if (agentFilter !== 'All' && r.agent !== agentFilter) return false;

      // date
      if (dateMode === 'All') return true;

      if (!r.calldate) return false; // if filtering by date but row has no date

      if (dateMode === 'Today') {
        return r.calldate >= todayStart && r.calldate <= todayEnd;
      }
      if (dateMode === 'Past') {
        return r.calldate < todayStart;
      }
      if (dateMode === 'Upcoming') {
        return r.calldate > todayEnd;
      }
      if (dateMode === 'Range') {
        if (!rangeStart && !rangeEnd) return true;
        const from = rangeStart ? startOfDay(new Date(rangeStart)) : new Date(-8640000000000000);
        const to = rangeEnd ? endOfDay(new Date(rangeEnd)) : new Date(8640000000000000);
        return r.calldate >= from && r.calldate <= to;
      }
      return true;
    });
  }, [rows, agentFilter, dateMode, rangeStart, rangeEnd]);

  const sortedFilteredRows = useMemo(() => {
    if (!callDateSort) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const da = getCallDateTime(a);
      const db = getCallDateTime(b);

      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;

      return callDateSort === 'asc' ? da - db : db - da;
    });
  }, [filteredRows, callDateSort]);


  // Nice label for range chip
  const rangeLabel = useMemo(() => {
    if (dateMode !== 'Range') return '';
    if (!rangeStart && !rangeEnd) return 'Range';
    const fmt = (s) => new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    if (rangeStart && rangeEnd) return `${fmt(rangeStart)} – ${fmt(rangeEnd)}`;
    if (rangeStart) return `${fmt(rangeStart)} →`;
    if (rangeEnd) return `← ${fmt(rangeEnd)}`;
    return 'Range';
  }, [dateMode, rangeStart, rangeEnd]);


  // NEW: open & fetch by id
  const openLead = async (id) => {
    setModalOpen(true);
    setModalLoading(true);
    setModalError('');
    setModalLead(null);
    try {
      const resp = await API.get(`/auth/lead/${id}`);
      // accept {lead} or raw object
      const lead = resp?.data?.lead || resp?.data;
      setModalLead(lead || null);
    } catch (e) {
      console.error(e);
      setModalError('Could not load lead details.');
    } finally {
      setModalLoading(false);
    }
  };


  const pageCount = Math.max(1, Math.ceil(sortedFilteredRows.length / pageSize));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedFilteredRows.slice(start, start + pageSize);
  }, [sortedFilteredRows, page, pageSize]);

  // Keep page in bounds if data shrinks
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);


  const applyLeadUpdateToRows = (updated) => {
    if (!updated) return;

    setRows(prev =>
      prev.map(r => {
        if (String(r.id) !== String(updated.id)) return r;

        // IMPORTANT: keep using your existing mapping shape
        const next = { ...r };

        if (updated.name != null) next.name = updated.name;
        if (updated.mobile != null) next.mobile = updated.mobile;
        if (updated.agent != null) next.agent = updated.agent;
        if (updated.timezone != null) next.timezone = updated.timezone;
        if (updated.ass != null) next.ass = updated.ass;
        if (updated.status != null) next.status = updated.status;
        if (updated.bname != null) next.bname = updated.bname;
        if (updated.calltime != null) next.calltime = updated.calltime;   // ✅ add


        // calldate may come as 'YYYY-MM-DD' (or ISO). Convert to Date for rendering/filtering.
        if (updated.calldate) next.calldate = new Date(updated.calldate);
        else if (updated.calldate === null) next.calldate = null;

        return next;
      })
    );

    // Also update the modal lead object so modal shows latest immediately
    setModalLead(prev => (prev && String(prev.id) === String(updated.id) ? { ...prev, ...updated } : prev));
  };


  return (
    <div className="cb-wrap">
      {/* HEADER */}
      <div className="cb-header">
        <div className="cb-header-left">
          <label className="cb-chip app-chip">
            <input type="radio" name="status" defaultChecked />
            <span>Win</span>
          </label>

          {/* FILTERS */}
          <div className="cb-filters">
            {/* Call Date mode buttons */}
            <div className="cb-filter-group">
              <button
                className={`cb-filter-chip ${dateMode === 'All' ? 'active' : ''}`}
                onClick={() => setDateMode('All')}
              >
                Call Date&nbsp;All
              </button>
              <button
                className={`cb-filter-chip ${dateMode === 'Today' ? 'active' : ''}`}
                onClick={() => setDateMode('Today')}
              >
                Today
              </button>
              <button
                className={`cb-filter-chip ${dateMode === 'Past' ? 'active' : ''}`}
                onClick={() => setDateMode('Past')}
              >
                Past
              </button>
              <button
                className={`cb-filter-chip ${dateMode === 'Upcoming' ? 'active' : ''}`}
                onClick={() => setDateMode('Upcoming')}
              >
                Upcoming
              </button>

              {/* Range picker chip */}
              <div className={`cb-filter-chip cb-filter-range ${dateMode === 'Range' ? 'active' : ''}`}>
                <button
                  className="cb-filter-range-toggle"
                  onClick={() => setDateMode('Range')}
                  title="Pick a date range"
                  type="button"
                >
                  {rangeLabel || 'Date Range'}
                </button>
                {dateMode === 'Range' && (
                  <div className="cb-range-pop">
                    <label>
                      From
                      <input
                        type="date"
                        value={rangeStart}
                        onChange={e => setRangeStart(e.target.value)}
                      />
                    </label>
                    <label>
                      To
                      <input
                        type="date"
                        value={rangeEnd}
                        onChange={e => setRangeEnd(e.target.value)}
                      />
                    </label>
                    <div className="cb-range-actions">
                      <button type="button" onClick={() => { setRangeStart(''); setRangeEnd(''); }}>
                        Clear
                      </button>
                      <button type="button" onClick={() => setDateMode('Range')}>
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Agent dropdown */}
            <div className="cb-filter-group">
              <select
                className="cb-filter-select"
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
              >
                <option value="All">Agent: All</option>
                {agentOptions.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* PAGINATION CONTROLS */}
        <div className="cb-pagination-bar">
          <div className="cb-page-size">
            <label>
              Show
              <select
                className='cb-filter-chip '
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={-1}>All</option>
              </select>
              rows
            </label>
            <span className="cb-page-meta">
              &nbsp;•&nbsp; {filteredRows.length} total
            </span>
          </div>

          <div className="cb-pager">
            <button
              type="button"
              className="cb-page-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              ‹ Prev
            </button>

            <span className="cb-page-status">
              Page {page} of {pageCount}
            </span>

            <button
              type="button"
              className="cb-page-btn"
              onClick={() => setPage(p => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              aria-label="Next page"
            >
              Next ›
            </button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="cb-table">
        <div className="cb-row cb-row-head">
          <div className="cb-col cb-col-name">
            <input
              className="cb-bullet"
              type="checkbox"
              aria-label="Select all"
              checked={allSelected}
              onChange={toggleAll}
            />
            Name
          </div>
          <div className="cb-col cb-col-biz">Business</div>
          <div className="cb-col cb-col-mobile">Mobile</div>
          <div className="cb-col cb-col-timezone">Timezone</div>
          <div className="cb-col cb-col-date cb-sortable">
            Call Date
            <span className="cb-sort-icons">
              <button
                type="button"
                className={callDateSort === 'asc' ? 'active' : ''}
                onClick={() => setCallDateSort('asc')}
                title="Sort ascending"
              >
                ↑
              </button>
              <button
                type="button"
                className={callDateSort === 'desc' ? 'active' : ''}
                onClick={() => setCallDateSort('desc')}
                title="Sort descending"
              >
                ↓
              </button>
            </span>
          </div>

          <div className="cb-col cb-col-agent">Agent</div>
          <div className="cb-col cb-col-status">Status</div>
          <div className="cb-col cb-col-ass">Assigned</div>
          <div className="cb-col cb-col-biz col-comment">Commnent</div>
        </div>

        {loading && (
          <div className="cb-row">
            <div className="cb-col cb-col-full">Loading…</div>
          </div>
        )}
        {err && (
          <div className="cb-row">
            <div className="cb-col cb-col-full cb-error">{err}</div>
          </div>
        )}
        {!loading && !err && filteredRows.length === 0 && (
          <div className="cb-row">
            <div className="cb-col cb-col-full">No items.</div>
          </div>
        )}

        {!loading && !err && paginatedRows.map((r) => {
          const isChecked = selectedIds.has(r.id);
          return (
            <div className="cb-row" key={r.id} onClick={() => openLead(r.id)}>
              <div className="cb-col cb-col-name">
                <input
                  className="cb-bullet"
                  type="checkbox"
                  aria-label={`Select ${r.name}`}
                  checked={isChecked}
                  onChange={() => toggleOne(r.id)}
                />
                {/* CLICKABLE NAME → fetch & open modal */}
                <button
                  className="cb-linklike"
                  onClick={() => openLead(r.id)}
                  title="View details"
                  type="button"
                >
                  {r.name}
                </button>
              </div>

              <div className="cb-col cb-col-biz">
                {r.bname || <span className="cb-muted">—</span>}
              </div>

              <div className="cb-col cb-col-mobile">
                {r.mobile || <span className="cb-muted">—</span>}
              </div>

              <div className="cb-col cb-col-timezone">
                {r.timezone || <span className="cb-muted">—</span>}
              </div>


              <div className="cb-col cb-col-date">
                {formatCallDate(r.calldate) || <span className="cb-muted">Add date</span>}
              </div>

              <div className="cb-col cb-col-agent">
                {r.agent || <span className="cb-muted">Assign</span>}
              </div>

              <div className="cb-col cb-col-status">
                {r.status ? (
                  <span className={`cb-status cb-status-${r.status.toLowerCase()}`}>{r.status}</span>
                ) : (
                  <span className="cb-muted">—</span>
                )}
              </div>

              <div className="cb-col cb-col-ass">
                {(() => {
                  const assMap = {
                    1: "Alan",
                    2: "Chris",
                    3: "David",
                    4: "Akshay",
                    5: "Mike",
                    6: "Cyrus",
                  };

                  const value = Number(r.ass);

                  return assMap[value] || "Not Assigned";
                })()}
              </div>
              <div className="cb-col cb-col-biz  col-comment">
                {r.scomment || <span className="cb-muted">—</span>}
              </div>
            </div>
          );
        })}
      </div>



      {/* MODAL MOUNT */}
      <LeadDetailsModal
        open={modalOpen}
        loading={modalLoading}
        error={modalError}
        lead={modalLead}
        onClose={() => setModalOpen(false)}
        onSaved={(updated) => {
          applyLeadUpdateToRows(updated);
        }}
      />

    </div>
  );

}
